// One eLab test still waiting for its result (KIN-2087), as the worklist reads it.
//
// The backend (KIN-2085) already decides what is waiting and what is overdue — it
// holds the turnaround figures, we do not — so nothing here recomputes either. What
// this file does is turn the DTO into the words and the ordering the worklist shows,
// and that is the part worth testing on its own.

export const LAB_TEST_STATUS = {
  outstanding: "OUTSTANDING",
  partial: "PARTIAL",
  resulted: "RESULTED",
  closed: "CLOSED",
};

// Only the two waiting statuses are ever shown. The labels are the GP's words, not the
// enum's: "OUTSTANDING" means nothing back yet, and "PARTIAL" means a preliminary
// result arrived and the final one is still due — which is not obvious from "Partial".
export const LAB_TEST_STATUS_CONFIG = {
  [LAB_TEST_STATUS.outstanding]: { label: "Waiting" },
  [LAB_TEST_STATUS.partial]: { label: "Preliminary result" },
  [LAB_TEST_STATUS.resulted]: { label: "Resulted" },
  [LAB_TEST_STATUS.closed]: { label: "Closed" },
};

export const getLabTestStatusLabel = (status) =>
  LAB_TEST_STATUS_CONFIG[status]?.label ?? "-";

// RESULTED and CLOSED never appear on a worklist. The backend already filters them
// out; this is the guard that keeps a stale cache or a changed backend from putting a
// finished test in front of a GP as if it were still due.
export const isWaitingLabTest = (test) =>
  test?.status === LAB_TEST_STATUS.outstanding ||
  test?.status === LAB_TEST_STATUS.partial;

// The lab's own name for the test when it gave one, its code when it did not. A GP can
// act on "Thyroid stimulating hormone" and sometimes on "TSH"; they can act on neither
// when both are missing, so the dash says so rather than leaving the cell blank.
export const getLabTestLabel = (test) => test?.testName || test?.testCode || "-";

const toTime = (value) => {
  const time = value ? new Date(value).getTime() : NaN;

  return Number.isNaN(time) ? null : time;
};

/**
 * Oldest first — the whole point of the list is the test nobody has noticed, and that
 * is the one at the top. A test with no requested time sorts last rather than first:
 * a missing date is not evidence of age, and letting it lead the list would push the
 * genuinely old tests down.
 */
export const sortByOldestFirst = (tests = []) =>
  [...tests].sort((first, second) => {
    const firstTime = toTime(first?.requestedAt);
    const secondTime = toTime(second?.requestedAt);

    if (firstTime === null && secondTime === null) return 0;
    if (firstTime === null) return 1;
    if (secondTime === null) return -1;

    return firstTime - secondTime;
  });

/**
 * The rows the practice worklist shows: waiting tests only, oldest first.
 */
export const toWorklistRows = (tests = []) =>
  sortByOldestFirst(tests.filter(isWaitingLabTest));

/*
 * The filter options are derived from the list itself rather than from a fixed table of
 * lab sections, because the section is a local code (OBR.24) that each hospital spells
 * its own way — a hard-coded list would offer sections nobody orders and hide the ones
 * they do. The cost is that an option exists only while something is waiting under it,
 * which is exactly when the filter is of any use.
 */
const toOptions = (values) =>
  [...new Set(values.filter(Boolean))].sort((first, second) =>
    String(first.label ?? first).localeCompare(String(second.label ?? second))
  );

export const getSectionOptions = (tests = []) =>
  toOptions(tests.map((test) => test?.section)).map((section) => ({
    value: section,
    label: section,
  }));

export const getOrderingGpOptions = (tests = []) => {
  const byId = new Map();

  tests.forEach((test) => {
    if (test?.orderingStaffId === null || test?.orderingStaffId === undefined) {
      return;
    }

    const id = String(test.orderingStaffId);

    if (!byId.has(id)) {
      byId.set(id, { value: id, label: test.orderingStaffName || `Staff ${id}` });
    }
  });

  return [...byId.values()].sort((first, second) =>
    first.label.localeCompare(second.label)
  );
};

/**
 * The three filters, applied together. Each is ignored when it is not set, so an
 * unfiltered list is the full worklist rather than an empty one.
 *
 * `orderingStaffId` is compared as a string: it arrives from the filter menu as the
 * option's value and from the API as a number, and `===` between the two is false.
 */
export const filterWorklistRows = (
  rows = [],
  { overdueOnly = false, orderingStaffId = null, section = null } = {}
) =>
  rows.filter((row) => {
    if (overdueOnly && !row?.overdue) return false;

    if (
      orderingStaffId !== null &&
      orderingStaffId !== undefined &&
      String(row?.orderingStaffId) !== String(orderingStaffId)
    ) {
      return false;
    }

    if (
      section &&
      String(row?.section ?? "").toUpperCase() !== String(section).toUpperCase()
    ) {
      return false;
    }

    return true;
  });

export const countOverdue = (tests = []) =>
  tests.filter((test) => test?.overdue).length;

/**
 * What one order on the patient's screen still owes, in the shape the ticket asked for:
 * "3 of 4 back. Vitamin D outstanding, overdue".
 *
 * `totalTests` is how many tests the order carries, which the order itself knows
 * (`testNames`). The outstanding endpoint reports only what is still waiting, so the
 * "back" figure is a subtraction — and when the order does not know its own tests yet
 * (Healthlink sends the names only once the completed order has been downloaded) the
 * count is left out instead of guessed, and the waiting tests are named on their own.
 */
export const summariseOrderOutstanding = ({ totalTests, outstandingTests = [] }) => {
  const waiting = outstandingTests.filter(isWaitingLabTest);
  const names = sortByOldestFirst(waiting).map(getLabTestLabel);
  const overdueCount = countOverdue(waiting);
  const isTotalKnown = Number.isFinite(totalTests) && totalTests > 0;

  if (waiting.length === 0) {
    return {
      outstandingCount: 0,
      overdueCount: 0,
      names: [],
      // Nothing is waiting. With a known total that is worth saying out loud; without
      // one there is nothing to say, and an empty cell is the honest answer.
      summary: isTotalKnown ? `All ${totalTests} back` : null,
    };
  }

  const backCount = isTotalKnown
    ? Math.max(totalTests - waiting.length, 0)
    : null;

  const countSentence =
    backCount === null
      ? `${waiting.length} outstanding`
      : `${backCount} of ${totalTests} back`;

  const namesSentence = `${names.join(", ")} outstanding${
    overdueCount > 0 ? ", overdue" : ""
  }`;

  return {
    outstandingCount: waiting.length,
    overdueCount,
    names,
    summary: `${countSentence}. ${namesSentence}`,
  };
};

/**
 * A close that the backend refused because the test had been resulted or closed in the
 * meantime (409). It is not an error the GP caused and not one they can fix — the list
 * they were looking at was simply out of date — so the caller refreshes rather than
 * reporting a failure.
 */
export const isAlreadySettledError = (error) =>
  error?.response?.status === 409 || error?.response?.data?.code === "CONFLICT";
